import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Checkbox,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Search,
} from "@carbon/react";
import TextField from "../../../components/carbon/Form/TextField";
import {
  useAddGroupMembers,
  useCreateUserGroup,
  useRemoveGroupMember,
  useUpdateUserGroup,
} from "../../../hooks/useUserGroups";
import { useUsersByCompany } from "../../../hooks/useUsers";
import { useNotification } from "../../../context";
import { UserGroup } from "../../../types";
import {
  userGroupValidationSchema,
  UserGroupFormValues,
} from "./userGroupValidationSchema";
import "./userGroupForm.scss";

type UserGroupFormProps = {
  group: UserGroup | null;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

/**
 * The create/edit group dialog, rebuilt on Carbon's `ComposedModal` (Task 13) in place of the
 * headlessui-based `Slide` side panel — there is no scoped Carbon side-drawer primitive in this
 * flow yet, and a name field plus a filterable member list fits comfortably in a modal, the same
 * way `ConfirmDialog` already uses `ComposedModal` for a lighter confirmation.
 *
 * The name field runs through React Hook Form + `userGroupValidationSchema` (its own module, same
 * convention as `newUserValidationSchema.ts`/`editUserValidationSchema.ts`). Membership stays a
 * plain `selectedIds` state, exactly as it was pre-Carbon: there's no rule to validate there, and
 * a Carbon `Checkbox` per roster row (inside a `Search`-filtered, scrollable list) replaces the
 * hand-rolled `<input type="checkbox">` list without changing that shape.
 *
 * Unlike `NewUserForm`/`EditUserForm`, saving here does NOT go through a second `ConfirmDialog`
 * step — the pre-Carbon form never had one either (only *deleting* a group does, in
 * `UserGroups.tsx`), so this preserves that rather than adding a confirmation nothing asked for.
 * `ModalFooter`'s own `loadingStatus`/`primaryButtonDisabled` cover the in-flight state instead,
 * the same guard `ConfirmDialog` gives its own confirm button.
 */
const UserGroupForm = ({ group, open, setOpen }: UserGroupFormProps) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const { data: companyUsers } = useUsersByCompany("");

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [memberFilter, setMemberFilter] = useState("");

  const close = () => setOpen(false);

  // Prefer the server's own message (e.g. the duplicate-name check) and only fall back to a
  // generic string when the response carries none — carried over from the pre-Carbon form.
  const getErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.message || fallback;

  const handleSaveError = (error: any) =>
    openNotification(getErrorMessage(error, t("userGroups.saveError")));

  const handleMembersError = (error: any) =>
    openNotification(getErrorMessage(error, t("userGroups.membersError")));

  const createGroup = useCreateUserGroup(close, handleSaveError);
  // Rename and membership changes are separate calls on edit; the dialog must stay open until
  // every one of them has actually settled, so none of these auto-close on their own success —
  // `onValid` closes once all have resolved. Same shape as the pre-Carbon form.
  const updateGroup = useUpdateUserGroup(undefined, handleSaveError);
  const addMembers = useAddGroupMembers(undefined, handleMembersError);
  const removeMember = useRemoveGroupMember(undefined, handleMembersError);

  const isSaving =
    createGroup.isPending ||
    updateGroup.isPending ||
    addMembers.isPending ||
    removeMember.isPending;

  const defaultValues: UserGroupFormValues = useMemo(
    () => ({ name: group?.name ?? "" }),
    [group]
  );

  const { control, handleSubmit, reset } = useForm<UserGroupFormValues>({
    resolver: yupResolver(userGroupValidationSchema),
    defaultValues,
  });

  // Re-seed the form whenever it opens for a different group (or opens fresh for a new one) —
  // same reasoning as EditUserForm.tsx's own reset effect, and the same [group, open] dependency
  // the pre-Carbon version keyed its own reset effect on.
  useEffect(() => {
    reset(defaultValues);
    setSelectedIds(group?.members.map((m) => m.id) ?? []);
    setMemberFilter("");
  }, [group, open, reset, defaultValues]);

  const filteredUsers = useMemo(() => {
    const term = memberFilter.trim().toLowerCase();
    return (companyUsers ?? []).filter((u: any) => {
      if (!term) return true;
      return (
        u.fullName?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term)
      );
    });
  }, [companyUsers, memberFilter]);

  const toggle = (id: string) =>
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((existing) => existing !== id)
        : [...current, id]
    );

  const onValid = async (values: UserGroupFormValues) => {
    if (!group) {
      createGroup.mutate({ name: values.name, memberIds: selectedIds });
      return;
    }

    const before = group.members.map((m) => m.id);
    const added = selectedIds.filter((id) => !before.includes(id));
    const removed = before.filter((id) => !selectedIds.includes(id));

    try {
      // Rename plus every membership change must all apply before the dialog closes —
      // otherwise a fast rename can close it while an add or remove is still in flight (or has
      // failed) behind it.
      await Promise.all([
        updateGroup.mutateAsync({ id: group.id, name: values.name }),
        ...(added.length
          ? [addMembers.mutateAsync({ id: group.id, userIds: added })]
          : []),
        ...removed.map((userId) =>
          removeMember.mutateAsync({ id: group.id, userId })
        ),
      ]);
      close();
    } catch {
      // Each mutation's own onError already surfaced a notification with the failure reason;
      // keep the dialog open so the user can see it and retry rather than silently losing
      // whichever part of the edit didn't apply.
    }
  };

  const title = group ? t("userGroups.edit") : t("userGroups.add");

  return (
    <ComposedModal
      open={open}
      size="md"
      aria-label={title}
      onClose={() => {
        close();
      }}
    >
      <ModalHeader title={title} />
      <ModalBody hasForm>
        <TextField
          name="name"
          label={t("userGroups.name")}
          placeholder={t("userGroups.namePlaceholder")}
          control={control}
          required
        />

        <div className="user-group-form__members">
          {/* `name` is the group's one required field; Members is the minority-optional one on
              this majority-required form (this task's brief), so it gets the same "(optional)"
              marker `TextField`/`SelectField` append internally — reusing `form.optional` here
              rather than inventing a second string for the same wording, since this section
              label isn't one of those primitives (it's a plain heading over a Search + Checkbox
              list, not an RHF-bound field). See TextField.tsx's docstring for Carbon's full
              required/optional-marking guidance. */}
          <p className="user-group-form__members-label">
            {t("userGroups.members")} {t("form.optional")}
          </p>
          <Search
            className="user-group-form__search"
            labelText={t("userGroups.searchMembers")}
            placeholder={t("userGroups.searchMembers")}
            value={memberFilter}
            onChange={(event) => setMemberFilter(event.target.value)}
            onClear={() => setMemberFilter("")}
            size="md"
          />
          <div className="user-group-form__member-list">
            {filteredUsers.length === 0 ? (
              <p className="user-group-form__no-matches">
                {t("table.noResultsFound")}
              </p>
            ) : (
              filteredUsers.map((companyUser: any) => (
                <div
                  key={companyUser.id}
                  className="user-group-form__member-row"
                >
                  <Checkbox
                    id={`user-group-member-${companyUser.id}`}
                    labelText={
                      <>
                        {companyUser.fullName}
                        <span className="user-group-form__member-email">
                          {companyUser.email}
                        </span>
                      </>
                    }
                    checked={selectedIds.includes(companyUser.id)}
                    onChange={() => toggle(companyUser.id)}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter
        secondaryButtonText={t("cta.cancel")}
        primaryButtonText={isSaving ? t("cta.saving") : t("cta.save")}
        primaryButtonDisabled={isSaving}
        loadingStatus={isSaving ? "active" : "inactive"}
        loadingDescription={t("cta.loading")}
        onRequestSubmit={handleSubmit(onValid)}
      >
        {null}
      </ModalFooter>
    </ComposedModal>
  );
};

export default UserGroupForm;
